/**
 * UploadArea Component
 * Shared drag-and-drop file upload area with consistent design across the app
 */

import { useRef } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import './UploadArea.css';

interface UploadAreaProps {
  uploading?: boolean;
  uploadProgress?: number;
  dragOver?: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileSelect: (files: FileList) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
}

export function UploadArea({
  uploading = false,
  uploadProgress,
  dragOver = false,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileSelect,
  accept,
  multiple = true,
  disabled = false,
  className = '',
}: UploadAreaProps) {
  const { t } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    if (!uploading && !disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onFileSelect(e.target.files);
      e.target.value = '';
    }
  };

  const uploadingText = uploadProgress != null
    ? `${t('common.upload.uploading')} ${Math.round(uploadProgress)}%`
    : t('common.upload.uploading');

  return (
    <div
      className={`upload-area ${dragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''} ${disabled ? 'disabled' : ''} ${className}`}
      onClick={handleClick}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
      {uploading ? (
        <div className="upload-area-loading">
          <div className="upload-spinner" />
          <p className="upload-text">{uploadingText}</p>
        </div>
      ) : (
        <div className="upload-area-idle">
          <div className="upload-icon-wrapper">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" strokeLinecap="round" strokeLinejoin="round" />
              <polyline points="17 8 12 3 7 8" strokeLinecap="round" strokeLinejoin="round" />
              <line x1="12" y1="3" x2="12" y2="15" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p className="upload-text">{t('common.upload.chooseFiles')}</p>
          <p className="upload-subtext">{t('common.upload.dragAndDrop')}</p>
        </div>
      )}
    </div>
  );
}
